
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import { AppHeader } from '../features/chat/components/WelcomeView';
import WelcomeView from '../features/chat/components/WelcomeView';
import ChatContainer from '../features/chat/components/ChatContainer';
import IntegrationsPage from './IntegrationsPage';
import SettingsPage from './SettingsPage';
import PricingPage from './PricingPage';
import SchedulesDashboard from './SchedulesDashboard';
import ProjectHome from '../features/projects/ProjectHome';
import CreateProjectModal from '../features/projects/CreateProjectModal';
import ProjectsPage from './ProjectsPage';
import { Message, ChatSession, ViewState, ConnectedApp, User, ToolApproval, UsageStats, AUTO_TOOLS, ApprovalMode, Project, ProjectTab, ProjectPlanStep, ProjectActivityTimeline, ProjectKnowledgeBase, ExecutionLedger } from '../types';
import { api } from '../services/api';
import { supabase } from '../lib/supabaseClient';
import { hasScheduleIntent, stripScheduleCommand } from '../services/scheduleParser';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  // App State
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [streamingThinking, setStreamingThinking] = useState<string>('');
  const [streamingResponse, setStreamingResponse] = useState<string>('');
  const [processingStatus, setProcessingStatus] = useState<'thinking' | 'executing' | 'analyzing' | ''>('');
  const [activeLedger, setActiveLedger] = useState<ExecutionLedger | null>(null);
  const activeLedgerRef = React.useRef<ExecutionLedger | null>(null);

  const handleLedgerUpdate = useCallback((ledger: ExecutionLedger | null) => {
      activeLedgerRef.current = ledger;
      setActiveLedger(ledger);
  }, []);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [schedulePrefillText, setSchedulePrefillText] = useState('');
  const [isScheduleMode, setIsScheduleMode] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>(() => {
    const path = window.location.pathname;
    if (path === '/schedule') return 'schedules';
    if (path === '/projects') return 'projects';
    if (path.startsWith('/chat/') && path.length > 6) return 'chat';
    if (path.startsWith('/project/') && path.length > 9) return 'project-home';
    return 'welcome';
  });
  const [appToDisconnect, setAppToDisconnect] = useState<ConnectedApp | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ─── Project State ──────────────────────────────────────────
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [activeProjectTab, setActiveProjectTab] = useState<ProjectTab>('overview');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [projectSessions, setProjectSessions] = useState<ChatSession[]>([]);
  const [planSteps, setPlanSteps] = useState<ProjectPlanStep[]>([]);
  const [activities, setActivities] = useState<ProjectActivityTimeline[]>([]);
  const [knowledge, setKnowledge] = useState<ProjectKnowledgeBase[]>([]);
  
  // Data from API
  const [user, setUser] = useState<User | null>(null);

  // ── Connected Apps with localStorage persistence ──
  // Initialise immediately from cache so UI shows correct status before
  // the async network fetch completes (prevents the race-condition flash).
  const APPS_CACHE_KEY = 'neura_connected_apps_v1';
  const readAppsCache = (): ConnectedApp[] => {
    try {
      const raw = localStorage.getItem(APPS_CACHE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };
  const writeAppsCache = (apps: ConnectedApp[]) => {
    try { localStorage.setItem(APPS_CACHE_KEY, JSON.stringify(apps)); } catch {}
  };

  const [connectedApps, setConnectedAppsRaw] = useState<ConnectedApp[]>(readAppsCache);
  // Wrap setter so every update also persists to localStorage
  const setConnectedApps = (appsOrUpdater: ConnectedApp[] | ((prev: ConnectedApp[]) => ConnectedApp[])) => {
    setConnectedAppsRaw(prev => {
      const next = typeof appsOrUpdater === 'function' ? (appsOrUpdater as (p: ConnectedApp[]) => ConnectedApp[])(prev) : appsOrUpdater;
      writeAppsCache(next);
      return next;
    });
  };

  // If the cache is non-empty, apps are already "loaded" (from a previous session).
  // This means returning users get accurate auth checks immediately without waiting for the network.
  const appsLoaded = React.useRef<boolean>(
    (() => { try { return !!localStorage.getItem(APPS_CACHE_KEY); } catch { return false; } })()
  );


  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
      const path = window.location.pathname;
      if (path.startsWith('/chat/') && path.length > 6) return path.split('/chat/')[1];
      return null;
  });
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = React.useRef(false);
  const lastSyncTime = React.useRef<{ [key: string]: number }>({});

  const handleSelectProject = useCallback(async (project: Project) => {
    setActiveProject(project);
    setActiveProjectTab('overview');
    setCurrentView('project-home');
    setProjectSessions([]);
    if (window.innerWidth < 768) setIsSidebarExpanded(false);

    const url = `/project/${project.id}`;
    if (window.location.pathname !== url) {
      window.history.pushState(null, '', url);
    }

    try {
      const context = await api.getProjectContext(project.id);
      if (context) {
        setPlanSteps(context.planSteps);
        setActivities(context.activities);
        setKnowledge(context.knowledge);
      }
    } catch (e) {
      console.warn("Failed to load project context", e);
    }
  }, []);

  // Initial Data Fetch
  const fetchInitialData = async () => {
    // Only show the hard loading splash on the very first visit
    if (!user) setIsLoading(true);

    try {
        // 1. Critical User Fetch (Determines Plan/Credits/Name)
        const userData = await api.getUser();
        setUser(userData);
        
        // At this point we have the user profile, we can reveal the layout
        setIsLoading(false);

        // 2. Fetch non-critical data in the background (Optimistic)
        // We handle these individually so one slow fetch doesn't block the rest
        
        api.getIntegrations()
            .then(apps => { 
                setConnectedApps(apps);
                appsLoaded.current = true;
                lastSyncTime.current['integrations'] = Date.now();
            })
            .catch(e => {
                // Even on failure, mark loaded so we rely on the cache
                appsLoaded.current = true;
                console.warn("Background Integrations fetch failed:", e);
            });

        api.getUsage()
            .then(setUsageStats)
            .catch(e => console.warn("Background Usage fetch failed:", e));

        api.getSessions()
            .then(sessionsData => {
                setSessions(sessionsData);
                
                // Deep Link Logic (moved inside session load completion)
                const path = window.location.pathname;
                if (path.startsWith('/chat/') && path.length > 6) {
                    const sessionIdFromUrl = path.split('/chat/')[1];
                    const exists = sessionsData.find(s => s.id === sessionIdFromUrl);
                    if (exists) {
                        handleLoadSessionFromInit(sessionIdFromUrl, sessionsData);
                    } else {
                        window.history.replaceState(null, '', '/chat');
                    }
                }
            })
            .catch(e => console.warn("Background Sessions fetch failed:", e));

        // 3. Fetch projects in the background
        api.getProjects()
            .then(projectsData => {
                setProjects(projectsData);
                
                const path = window.location.pathname;
                if (path.startsWith('/project/') && path.length > 9) {
                  const projectIdFromUrl = path.split('/project/')[1];
                  const projectObj = projectsData.find(p => p.id === projectIdFromUrl);
                  if (projectObj) {
                    handleSelectProject(projectObj);
                  } else {
                    window.history.replaceState(null, '', '/');
                  }
                }
            })
            .catch(e => console.warn("Background Projects fetch failed:", e));

    } catch (error) {
        console.error("Critical app load failure:", error);
    } finally {
        setIsLoading(false);
    }
  };

  const handleLoadSessionFromInit = async (id: string, allSessions: ChatSession[]) => {
      const cachedSession = allSessions.find(s => s.id === id);
      if (cachedSession) {
          setMessages(cachedSession.messages);
          setCurrentSessionId(cachedSession.id);
      }
      setCurrentView('chat');
      if (window.innerWidth < 768) setIsSidebarExpanded(false);

      try {
          const freshSession = await api.getSessionDetails(id);
          if (freshSession) {
              setMessages(freshSession.messages);
              setSessions(prev => prev.map(s => s.id === id ? freshSession : s));
          }
      } catch (e) {
          console.warn("Failed to load fresh session details", e);
      }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    fetchInitialData();
  }, []);

  // Handle OAuth success message from the callback popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
        if (event.data === 'oauth_success') {
            console.log("[Dashboard] OAuth Success detected, re-syncing data...");
            // Re-fetch everything to ensure consistent state
            fetchInitialData();
            // Specifically refresh integrations for the UI
            api.getIntegrations().then(apps => {
                setConnectedApps(apps);
                lastSyncTime.current['integrations'] = Date.now();
            }).catch(console.warn);
        }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [user]);

  // Sync integrations when navigating to relevant screens (with a basic cache)
  useEffect(() => {
    const now = Date.now();
    const lastSync = lastSyncTime.current['integrations'] || 0;
    
    // Only re-sync if entering integrations view and we haven't synced in the last 5 minutes
    if (currentView === 'integrations' && (now - lastSync > 300000)) {
        const syncApps = async () => {
            try {
                const apps = await api.getIntegrations();
                setConnectedApps(apps);
                lastSyncTime.current['integrations'] = now;
            } catch (e) {
                console.warn("Syncing integrations failed", e);
            }
        };
        syncApps();
    }
  }, [currentView]);

  // Sync URL with currentSessionId / View State
  useEffect(() => {
      // Don't sync URL until the initial load is done to avoid wiping deep links
      if (isLoading && !user) return;

      if (currentView === 'welcome') {
          if (window.location.pathname !== '/chat') {
              window.history.pushState(null, '', '/chat');
          }
      } else if (currentView === 'schedules') {
          if (window.location.pathname !== '/schedule') {
              window.history.pushState(null, '', '/schedule');
          }
      } else if (currentView === 'projects') {
          if (window.location.pathname !== '/projects') {
              window.history.pushState(null, '', '/projects');
          }
      } else if (currentView === 'chat' && currentSessionId) {
          const expectedPath = `/chat/${currentSessionId}`;
          if (window.location.pathname !== expectedPath) {
              window.history.pushState(null, '', expectedPath);
          }
      }
  }, [currentSessionId, currentView, isLoading, user]);

  // Listen to Supabase Realtime for backend-generated messages
  useEffect(() => {
      if (!currentSessionId || !supabase) return;

      const channel = supabase
        .channel(`messages-${currentSessionId}`)
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'messages', filter: `session_id=eq.${currentSessionId}` },
            async (payload: any) => {
                console.log('[Realtime] Message update received:', payload);
                const freshSession = await api.getSessionDetails(currentSessionId);
                if (freshSession) {
                    setMessages(freshSession.messages);
                    const lastLedger = freshSession.messages.slice().reverse().find((m: any) => m.ledger)?.ledger;
                    handleLedgerUpdate(lastLedger || null);
                }
            }
        )
        .subscribe();

      return () => {
          supabase.removeChannel(channel);
      };
  }, [currentSessionId, handleLedgerUpdate]);

  // Polling removed — replaced by SSE streaming in sendMessage.

  // Handle browser back button (popstate)
  useEffect(() => {
      const handlePopState = () => {
          const path = window.location.pathname;
          if (path === '/schedule') {
              setCurrentView('schedules');
              if (window.innerWidth < 768) setIsSidebarExpanded(false);
          } else if (path === '/projects') {
              setCurrentView('projects');
              setActiveProject(null);
              if (window.innerWidth < 768) setIsSidebarExpanded(false);
          } else if (path.startsWith('/project/')) {
              const id = path.split('/project/')[1];
              const projectObj = projects.find(p => p.id === id);
              if (projectObj) {
                  handleSelectProject(projectObj);
              }
          } else if (path === '/chat' || path === '/') {
              handleNewChat(true); // pass flag to skip pushing state since we're popping
          } else if (path.startsWith('/chat/')) {
              const id = path.split('/chat/')[1];
              handleLoadSessionFromPopState(id);
          }
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
  }, [sessions, projects, handleSelectProject]); // depends on sessions/projects to load

  const handleLoadSessionFromPopState = async (id: string) => {
    const cachedSession = sessions.find(s => s.id === id);
    if (cachedSession) {
        setMessages(cachedSession.messages);
        setCurrentSessionId(cachedSession.id);
    }
    setCurrentView('chat');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);

    try {
        const freshSession = await api.getSessionDetails(id);
        if (freshSession) {
            setMessages(freshSession.messages);
            setSessions(prev => prev.map(s => s.id === id ? freshSession : s));
        }
    } catch (e) {
        console.warn("Failed to load fresh session details popstate", e);
    }
  };

  // Autonomous Execution Loop Removed

  const handleToggleApp = async (id: string) => {
    const app = connectedApps.find(a => a.id === id);
    if (!app) return;

    if (app.status === 'connected') {
        setAppToDisconnect(app);
        return;
    }

    // --- Limit Check for Free Plan ---
    const userPlan = user?.plan?.toLowerCase() || 'free';
    if (userPlan === 'free') {
        const connectedCount = connectedApps.filter(a => a.status === 'connected').length;
        if (connectedCount >= 3) {
            alert("Free plan is limited to 3 active integrations. Please upgrade to Pro or Premium for unlimited connections.");
            setCurrentView('pricing');
            return;
        }
    }

    setConnectedApps(prev => prev.map(a => 
        a.id === id ? { ...a, status: 'connected' } : a
    ));

    try {
        const updatedApps = await api.toggleIntegration(id, 'connect');
        setConnectedApps(updatedApps);
    } catch (error) {
        console.error("Failed to toggle integration", error);
        setConnectedApps(prev => prev.map(a => 
            a.id === id ? { ...a, status: 'connect' } : a
        ));
        alert(`Failed to connect. Please check the console.`);
    }
  };

  const confirmDisconnect = async () => {
      if (!appToDisconnect) return;
      const id = appToDisconnect.id;
      setAppToDisconnect(null);

      // Optimistic disconnect update
      setConnectedApps(prev => prev.map(app => 
          app.id === id ? { ...app, status: 'connect' } : app
      ));

      try {
          const updatedApps = await api.toggleIntegration(id, 'disconnect');
          setConnectedApps(updatedApps);
      } catch (error) {
          console.error("Failed to disconnect integration", error);
          setConnectedApps(prev => prev.map(app => 
              app.id === id ? { ...app, status: 'connected' } : app
          ));
          alert(`Failed to disconnect ${id}.`);
      }
  };



  // Ref to hold the current abort controller
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        setIsProcessing(false);
        setStreamingThinking('');
        setStreamingResponse('');
        setProcessingStatus('');
        // Add a system message indicating cancellation
        setMessages(prev => [...prev, { id: 'sys-' + Date.now(), type: 'system', content: 'Stopped by user.', timestamp: new Date() }]);
    }
  }, []);

  const handleSend = useCallback(async (text: string, file?: File, interventionId?: string, interventionAnswer?: any, isSilentChain: boolean = false) => {
    if (!text.trim() && !file && !interventionAnswer && !isSilentChain) return;

    // ─── Schedule Mode: redirect to schedules page with text as prefill ───
    if (!isSilentChain && isScheduleMode && text.trim()) {
        setSchedulePrefillText(text.trim());
        setCurrentView('schedules');
        setInputValue('');
        setIsScheduleMode(false);
        return;
    }

    // ─── /schedule Command Detection ───
    if (!isSilentChain && hasScheduleIntent(text)) {
        setCurrentView('schedules');
        setInputValue('');
        return;
    }

    if (currentView !== 'chat') {
        setCurrentView('chat');
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
    }
    // Create new controller
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Optimistic UI
    if (!isSilentChain) {
        const tempUserMsg: Message = {
            id: Date.now().toString(),
            type: 'user',
            content: text || (interventionAnswer ? String(interventionAnswer) : ''),
            timestamp: new Date(),
            attachment: file ? {
                name: file.name,
                type: file.type,
                size: file.size,
                url: URL.createObjectURL(file)
            } : undefined
        };
        setMessages(prev => [...prev, tempUserMsg]);
        setInputValue('');
    }
    
    // Check for credits
    if (usageStats && usageStats.requests <= 0) {
        const nextRank = user?.plan === 'free' ? 'Pro' : 'Premium';
        const upgradeMsg: Message = {
            id: 'upgrade-' + Date.now(),
            type: 'ai',
            content: `You've reached your credit limit for this month. Upgrade to **${nextRank}** to continue using Neura AI.`,
            timestamp: new Date(),
            toolApproval: {
                id: 'pricing-prompt',
                toolName: 'system_upgrade',
                args: { target: nextRank },
                status: 'pending',
                description: `Upgrade to ${nextRank} Plan`
            }
        };
        setMessages(prev => [...prev, upgradeMsg]);
        setInputValue('');
        return;
    }

    setIsProcessing(true);
    setStreamingThinking('');
    setStreamingResponse('');
    setProcessingStatus(isSilentChain ? 'analyzing' : 'thinking');
    let systemMsgId = '';
    // We use a local systemMsgId for state tracking but don't add the "Analyzing" text message
    if (!isSilentChain) {
        systemMsgId = 'system-thinking-' + Date.now();
    }
    
    // Pass systemMsgId to keep track of it if needed
    (controller as any)._systemMsgId = systemMsgId;

    // Live thinking callback - receives streamed thought tokens
    const handleThinkingUpdate = (thinkingText: string) => {
        if (thinkingText === '__DONE__') {
            setStreamingThinking('');
            setStreamingResponse('');
        } else {
            setStreamingThinking(thinkingText);
        }
    };

    // Live response callback - receives streamed response tokens for real-time display
    const handleResponseUpdate = (responseText: string) => {
        setStreamingResponse(responseText);
    };

    // Live tool event callback - receives tool start/result/pending events
    const handleToolEvent = (event: any) => {
        switch (event.type) {
            case 'ledger':
                setMessages(prev => [...prev, {
                    id: `ledger-${Date.now()}`,
                    type: 'ai' as const,
                    content: event.content || 'Execution plan created.',
                    timestamp: new Date(),
                    ledger: event.ledger
                }]);
                break;
            case 'ledger_update':
                setMessages(prev => {
                    const updated = [...prev];
                    for (let i = updated.length - 1; i >= 0; i--) {
                        if (updated[i].ledger) {
                            updated[i] = { ...updated[i], ledger: event.ledger };
                            break;
                        }
                    }
                    return updated;
                });
                break;
            case 'tool_start':
                setMessages(prev => [...prev, {
                    id: `tool-${Date.now()}`,
                    type: 'ai' as const,
                    content: event.content || '',
                    timestamp: new Date(),
                    toolApproval: {
                        id: `tool-${Date.now()}`,
                        toolName: event.tool,
                        args: event.args,
                        status: 'approved' as const,
                        description: `Execute ${event.tool}`
                    }
                }]);
                break;
            case 'tool_result':
                setMessages(prev => {
                    const updated = [...prev];
                    for (let i = updated.length - 1; i >= 0; i--) {
                        if (updated[i].toolApproval?.toolName === event.tool && updated[i].toolApproval?.status !== 'executed') {
                            updated[i] = { ...updated[i], toolApproval: event.approval };
                            break;
                        }
                    }
                    return updated;
                });
                break;
            case 'tool_pending':
                setMessages(prev => [...prev, {
                    id: event.message_id || `tool-${Date.now()}`,
                    type: 'ai' as const,
                    content: event.content || '',
                    timestamp: new Date(),
                    toolApproval: event.approval
                }]);
                break;
            case 'tool_error':
                setMessages(prev => [...prev, {
                    id: `error-${Date.now()}`,
                    type: 'system' as const,
                    content: `Tool ${event.tool} failed: ${event.error}`,
                    timestamp: new Date()
                }]);
                break;
        }
    };

    try {
      // Send message via SSE streaming — callbacks fire in real-time during the stream
      const { session, aiMsg } = await api.sendMessage(
        currentSessionId, 
        text, 
        file, 
        controller.signal, 
        handleThinkingUpdate, 
        handleResponseUpdate,
        handleLedgerUpdate, // onLedgerUpdate callback
        activeLedgerRef.current, // existingLedger
        handleToolEvent, // onToolEvent callback
        interventionId,
        interventionAnswer
      );
      
      setCurrentSessionId(session.id);
      
      setSessions(prev => {
          const exists = prev.find(s => s.id === session.id);
          if (exists) {
              return prev.map(s => s.id === session.id ? session : s);
          }
          return [session, ...prev];
      });

      if (isSilentChain) {
          aiMsg.isSilentChain = true;
      }

      // Preemptive Auth Check
      // Use a merged view: live connectedApps state (which starts pre-populated
      // from localStorage cache) ensures this check is always accurate — even on
      // first render before the network fetch completes.
      let isAuthRequired = false;
      if (aiMsg.toolApproval) {
          const t = aiMsg.toolApproval.toolName.toLowerCase();
          let requiresAppId = '';
          if (t.includes('email') || t.includes('gmail')) requiresAppId = 'gmail';
          else if (t.includes('calendar') || t.includes('event')) requiresAppId = 'calendar';
          else if (t.includes('doc')) requiresAppId = 'docs';
          else if (t.includes('sheet') || t.includes('spreadsheet')) requiresAppId = 'sheets';
          else if (t.includes('drive') || t.includes('file')) requiresAppId = 'drive';
          
          if (requiresAppId) {
              // Use live state (already seeded from localStorage cache on init)
              const app = connectedApps.find(a => a.id === requiresAppId);
              const anyConnected = connectedApps.some(a => a.status === 'connected');

              if (app?.status === 'expired') {
                  // Definitively expired — refresh token is dead, must reconnect
                  isAuthRequired = true;
                  aiMsg.toolApproval.status = 'auth_required';
              } else if (!anyConnected && appsLoaded.current) {
                  // Live fetch has completed and NO Google app is connected at all
                  // (not a loading race — the user genuinely hasn't connected anything)
                  isAuthRequired = true;
                  aiMsg.toolApproval.status = 'auth_required';
              }
              // If appsLoaded is false AND connectedApps is empty, we're mid-load —
              // let the tool proceed and the backend will surface a real error.
          }
      }



      // SSE stream is done. Add the final aiMsg if it has a toolApproval or content.
      setMessages(prev => {
          const filtered = prev.filter(m => m.id !== systemMsgId);
          if (aiMsg.toolApproval || aiMsg.content) {
              return [...filtered, aiMsg];
          }
          return filtered;
      });

      // Stream is complete — clean up processing state
      setIsProcessing(false);
      setStreamingThinking('');
      setStreamingResponse('');
      setProcessingStatus('');
      abortControllerRef.current = null;

      // Refresh from Supabase to get clean final state with real IDs
      const freshSession = await api.getSessionDetails(session.id);
      if (freshSession) {
          setMessages(freshSession.messages);
          const lastLedger = freshSession.messages.slice().reverse().find((m: any) => m.ledger)?.ledger;
          handleLedgerUpdate(lastLedger || null);
      }

      // Refresh credit usage after completion
      api.getUsage().then(setUsageStats).catch(console.warn);

  } catch (error: any) {
        setIsProcessing(false);
        setStreamingThinking('');
        setStreamingResponse('');
        setProcessingStatus('');
        abortControllerRef.current = null;
        if (error.name === 'AbortError' || error.message === 'AbortError' || error.message?.includes('cancelled')) {
            console.log("Request cancelled");
             setMessages(prev => prev.filter(m => m.id !== systemMsgId)); 
        } else {
            console.error("Failed to send message", error);
            setMessages(prev => [
                ...prev.filter(m => m.id !== systemMsgId),
                { id: 'error', type: 'system', content: 'Error connecting to Neura Agent. Please check your connection.', timestamp: new Date() }
            ]);
        }
    } finally {
        // State is now managed inside try/catch blocks for precision
    }
  }, [currentSessionId, currentView, connectedApps]);

  // Handle Approving a Pending Tool Action
  const handleApproveAction = async (messageId: string, approval: ToolApproval, updatedArgs?: any) => {
      if (!currentSessionId) return;
      
      // Cancel any previous and create new for execution
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      setIsProcessing(true);
      setProcessingStatus('executing');

      // Merge updated args if provided
      const actionApproval = {
          ...approval,
          args: updatedArgs || approval.args
      };

      // Update UI state to 'approved' immediately (optimistic)
      setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
              const { _autoExecute, ...restToolApproval } = (msg.toolApproval || approval) as any;
              return { ...msg, toolApproval: { ...restToolApproval, args: actionApproval.args, status: 'approved' as const } };
          }
          return msg;
      }));

      // Persist the approval status to Supabase immediately so it survives page reloads
      api.updateApprovalStatus(approval.id, 'executed', actionApproval.args).catch(console.warn);

      // Use a persistent internal state for "processing" but don't show a text message bubble
      const executingMsgId = 'sys-executing-' + Date.now();

      try {
          const responseMsg = await api.executeAction(currentSessionId, actionApproval, 'approve', undefined, messageId, controller.signal);
          
          // Sync state: Mark original as executed and ADD the new result message
          setMessages(prev => {
              const withUpdatedOriginal = prev.map(msg => {
                  if (msg.id === messageId) {
                      const newApproval = msg.toolApproval ? { ...msg.toolApproval, status: 'executed' as const } : undefined;
                      return { ...msg, toolApproval: newApproval };
                  }
                  return msg;
              });
              
              return [...withUpdatedOriginal, responseMsg];
          });

          // Update the active ledger if one exists
          if (activeLedgerRef.current) {
              const updated = { ...activeLedgerRef.current };
              const currentIndex = updated.steps.findIndex(s => s.status === 'PENDING' || s.status === 'IN_PROGRESS');
              if (currentIndex !== -1) {
                  updated.steps[currentIndex].status = 'COMPLETED';
                  updated.steps[currentIndex].output_summary = responseMsg.toolApproval?.result ? responseMsg.toolApproval.result.substring(0, 100) : 'Done';
              }
              activeLedgerRef.current = updated;
              setActiveLedger(updated);
          }

          // CHAIN EXECUTION: Handled by backend background worker
      } catch (error: any) {
          console.warn("Execute Action Error:", error);
          setMessages(prev => {
              let errorMsg = error.message || String(error);
              const isAuthError = errorMsg.includes('AUTH_EXPIRED') || errorMsg.includes('invalid_grant') || errorMsg.includes('No connected');
              
              const updatedMessages = prev.map(msg => {
                if (msg.id === messageId && isAuthError) {
                  return { 
                    ...msg, 
                    toolApproval: msg.toolApproval ? { ...msg.toolApproval, status: 'auth_required' as any } : undefined 
                  };
                }
                return msg;
              });

              if (isAuthError) return updatedMessages;

              // Map common error patterns for other errors
              if (errorMsg.includes('No connected') || errorMsg.includes('connect it in the Integrations')) {
                  errorMsg = 'This action requires a connected account. Please visit the Integrations tab to link your Google Workspace.';
              }

              return [...updatedMessages, { 
                  id: 'err-' + Date.now(), 
                  type: 'system', 
                  content: `Execution failed: ${errorMsg}`, 
                  timestamp: new Date() 
              }];
          });
          setIsProcessing(false);
          setProcessingStatus('');
      }
  };

  const handleRejectAction = async (messageId: string, approval: ToolApproval) => {
      // Update UI state
      setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
              return { ...msg, toolApproval: { ...msg.toolApproval!, status: 'rejected' as const } };
          }
          return msg;
      }));

      // Persist to DB and backend
      try {
          const responseMsg = await api.executeAction(currentSessionId!, approval, 'reject', undefined, messageId);
          setMessages(prev => [...prev, responseMsg]);
      } catch (e) {
          console.warn("Failed to reject action:", e);
      }
  };

  const handleNewChat = (fromPopState = false) => {
    setMessages([]);
    setInputValue('');
    setCurrentSessionId(null);
    setIsProcessing(false);
    setCurrentView('welcome');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
    
    // Push state only if not from popstate
    if (!fromPopState) {
        if (window.location.pathname !== '/chat') {
            window.history.pushState(null, '', '/chat');
        }
    }
  };

  const handleLoadSession = async (id: string) => {
    const cachedSession = sessions.find(s => s.id === id);
    if (cachedSession) {
        setMessages(cachedSession.messages);
        setCurrentSessionId(cachedSession.id);
    }

    setCurrentView('chat');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);

    try {
        const freshSession = await api.getSessionDetails(id);
        if (freshSession) {
            setMessages(freshSession.messages);
            setSessions(prev => prev.map(s => s.id === id ? freshSession : s));
        }
    } catch (e) {
        console.warn("Failed to load fresh session details", e);
    }
  };

  const handleDeleteSession = async (id: string) => {
      // Optimistic delete
      setSessions(prev => prev.filter(s => s.id !== id));
      
      if (currentSessionId === id) {
          handleNewChat();
      }

      try {
          await api.deleteSession(id);
      } catch (error) {
          console.error("Failed to delete session", error);
          // Re-fetch to sync if failed
          const sessionsData = await api.getSessions();
          setSessions(sessionsData);
      }
  };

  const handleClearHistory = async () => {
    // Optimistic clear
    setSessions([]);
    handleNewChat();
    setShowClearConfirm(false);

    try {
        await api.clearHistory();
    } catch (error) {
        console.error("Failed to clear history", error);
        const sessionsData = await api.getSessions();
        setSessions(sessionsData);
        alert("Failed to clear history from server.");
    }
  };

  const handleRenameSession = async (id: string, newTitle: string) => {
      // Optimistic rename
      setSessions(prev => prev.map(s => s.id === id ? { ...s, title: newTitle } : s));

      try {
          await api.renameSession(id, newTitle);
      } catch (error) {
          console.error("Failed to rename session", error);
          // Re-fetch to sync if failed
          const sessionsData = await api.getSessions();
          setSessions(sessionsData);
      }
  };

  const handleOpenIntegrations = () => {
    setCurrentView('integrations');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
  };

  const handleOpenSchedules = useCallback((prefill: string | any = '') => {
    const text = typeof prefill === 'string' ? prefill : '';
    setSchedulePrefillText(text);
    setCurrentView('schedules');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
  }, []);

  const handleToggleScheduleMode = useCallback(() => {
    setIsScheduleMode(prev => !prev);
  }, []);

  const handleOpenPricing = () => {
    setCurrentView('pricing');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
  };

  const handleOpenSettings = () => {
    setCurrentView('settings');
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
  };

  const handleUpdateName = async (name: string) => {
    try {
        await api.updateUser({ name });
        setUser(prev => prev ? { ...prev, name } : null);
    } catch (e) {
        console.warn("Failed to update name", e);
    }
  };

  const handleUpdatePlan = async () => {
      // Refresh user to get new plan and re-fetch usage to get new limits
      try {
          const userData = await api.getUser();
          setUser(userData);
          const stats = await api.getUsage();
          setUsageStats(stats);
      } catch (e) {
          console.warn("Failed to update dashboard after plan change", e);
      }
  };

  const handleCloseOverlay = useCallback(() => {
    if (activeProject) {
        setCurrentView('project-home');
    } else if (messages.length > 0) {
        setCurrentView('chat');
    } else {
        setCurrentView('welcome');
    }
    setSchedulePrefillText('');
  }, [messages, activeProject]);

  // ─── Project Handlers ───────────────────────────────────────

  const handleBackToProjects = useCallback(() => {
    setActiveProject(null);
    setActiveProjectTab('overview');
    setPlanSteps([]);
    setActivities([]);
    setKnowledge([]);
    setProjectSessions([]);
    
    if (window.location.pathname !== '/projects') {
      window.history.pushState(null, '', '/projects');
    }
    setCurrentView('projects');
  }, []);

  const handleOpenProjects = useCallback(() => {
    setCurrentView('projects');
    setActiveProject(null);
    if (window.innerWidth < 768) setIsSidebarExpanded(false);
  }, []);

  const handleProjectTabChange = useCallback((tab: ProjectTab) => {
    setActiveProjectTab(tab);
    setCurrentView('project-home');
  }, []);

  const handleCreateProject = useCallback(async (data: {
    name: string;
    description: string;
    icon: string;
    color: string;
  }) => {
    setIsCreatingProject(true);
    try {
      const newProject = await api.createProject(data);
      if (newProject) {
        setProjects(prev => [newProject, ...prev]);
        setShowCreateProject(false);
        // Auto-open the new project
        handleSelectProject(newProject);
      }
    } catch (e) {
      console.error('Failed to create project:', e);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  }, [handleSelectProject]);

  const handleProjectUpdate = useCallback((updated: Project) => {
    setActiveProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
  }, []);

  const handleProjectDelete = useCallback(async () => {
    if (!activeProject) return;
    const projectId = activeProject.id;
    try {
      await api.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      handleBackToProjects();
    } catch (e) {
      console.error('Failed to delete project:', e);
      alert('Failed to delete project. Please try again.');
    }
  }, [activeProject, handleBackToProjects]);

  if (isLoading && !user) {
      return (
          <div className="w-full h-screen flex items-center justify-center bg-white dark:bg-[#050505]">
              <div className="flex flex-col items-center gap-4 animate-pulse">
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-800 rounded-xl"></div>
                  <div className="text-black dark:text-white text-sm font-medium">Loading Workspace...</div>
              </div>
          </div>
      );
  }

  return (
    <div className="flex w-full h-screen bg-white dark:bg-[#050505] text-black dark:text-white font-sans overflow-hidden relative">
      
      {isSidebarExpanded && (
        <div 
            className="fixed inset-0 bg-black/20 z-40 md:hidden animate-in fade-in"
            onClick={() => setIsSidebarExpanded(false)}
        />
      )}

      <Sidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
        onNewChat={handleNewChat}
        onOpenIntegrations={handleOpenIntegrations}
        onOpenSchedules={handleOpenSchedules}
        onOpenSettings={handleOpenSettings}
        onOpenPricing={handleOpenPricing}
        currentView={currentView}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onLoadSession={handleLoadSession}
        onDeleteSession={handleDeleteSession}
        onRenameSession={handleRenameSession}
        onClearHistory={() => setShowClearConfirm(true)}
        usageStats={usageStats}
        userName={user?.name || 'Guest'}
        userPlan={user?.plan || 'free'}
        avatar={user?.avatar}
        projects={projects}
        activeProject={activeProject}
        activeProjectTab={activeProjectTab}
        onCreateProject={() => {
          setCurrentView('projects');
          setShowCreateProject(true);
        }}
        onSelectProject={handleSelectProject}
        onBackToProjects={handleBackToProjects}
        onProjectTabChange={handleProjectTabChange}
        projectSessions={projectSessions}
        onOpenProjects={handleOpenProjects}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarExpanded ? 'md:ml-64' : 'md:ml-16'} ml-0 w-full`}>

        {currentView !== 'integrations' && currentView !== 'settings' && currentView !== 'schedules' && currentView !== 'pricing' && currentView !== 'projects' && (
            <AppHeader
                isSidebarExpanded={isSidebarExpanded}
                onToggleSidebar={() => setIsSidebarExpanded(!isSidebarExpanded)}
                title={currentView === 'project-home' ? (activeProject?.name || 'Project') : 'Neura'}
            />
        )}

        <main className="flex-1 relative mt-0 overflow-hidden w-full h-full">
            {/* welcome view */}
            {currentView === 'welcome' && (
                <div className="h-full pt-16 md:pt-20 overflow-y-auto w-full">
                    <WelcomeView
                        inputValue={inputValue}
                        onInputChange={setInputValue}
                        onSend={handleSend}
                        userName={user?.name || 'Guest'}
                        onOpenIntegrations={handleOpenIntegrations}
                        onOpenSchedules={handleOpenSchedules}
                        isScheduleMode={isScheduleMode}
                        onToggleScheduleMode={handleToggleScheduleMode}
                        apps={connectedApps || []}
                        onToggleApp={handleToggleApp}
                        projects={projects}
                        onSelectProject={handleSelectProject}
                    />
                </div>
            )}

            {/* chat view */}
            {currentView === 'chat' && (
                <div className="h-full pt-16 md:pt-20 flex flex-col w-full">
                   <ChatContainer 
                        messages={messages}
                        inputValue={inputValue}
                        onInputChange={setInputValue}
                        onSend={handleSend}
                        onCancel={handleCancel}
                        isProcessing={isProcessing}
                        streamingThinking={streamingThinking}
                        streamingResponse={streamingResponse}
                        processingStatus={processingStatus}
                        onApproveAction={handleApproveAction}
                        onRejectAction={handleRejectAction}
                        onOpenIntegrations={handleOpenIntegrations}
                        onOpenPricing={handleOpenPricing}
                        onOpenSchedules={handleOpenSchedules}
                        isScheduleMode={isScheduleMode}
                        onToggleScheduleMode={handleToggleScheduleMode}
                        currentSessionId={currentSessionId || ''}
                    />
                </div>
            )}

            {/* schedules view */}
            {currentView === 'schedules' && (
                <div className="h-full w-full">
                    <SchedulesDashboard 
                      onClose={handleCloseOverlay} 
                      prefillPrompt={schedulePrefillText}
                      onClearPrefill={() => setSchedulePrefillText('')}
                      isActive={true}
                    />
                </div>
            )}

            {/* integrations view */}
            {currentView === 'integrations' && (
                <div className="h-full w-full">
                    <IntegrationsPage 
                        onClose={handleCloseOverlay} 
                        connectedApps={connectedApps}
                        onToggleApp={handleToggleApp}
                    />
                </div>
            )}

            {/* pricing view */}
            {currentView === 'pricing' && (
                <div className="h-full w-full">
                    <PricingPage 
                        onClose={handleCloseOverlay} 
                        currentPlan={user?.plan?.toLowerCase() as any}
                    />
                </div>
            )}

            {/* settings view */}
            {currentView === 'settings' && (
                <div className="h-full w-full">
                    <SettingsPage 
                      onClose={handleCloseOverlay} 
                      onManageIntegrations={handleOpenIntegrations}
                      onPricingClick={handleOpenPricing}
                      userName={user?.name || 'Guest'}
                      onUpdateName={handleUpdateName}
                      onUpdatePlan={handleUpdatePlan}
                      connectedApps={connectedApps}
                      avatar={user?.avatar}
                    />
                </div>
            )}

            {/* projects view */}
            {currentView === 'projects' && (
                <div className="h-full w-full pt-0">
                    <ProjectsPage 
                      projects={projects}
                      onSelectProject={handleSelectProject}
                      onCreateProject={() => setShowCreateProject(true)}
                      onToggleSidebar={() => setIsSidebarExpanded(!isSidebarExpanded)}
                    />
                </div>
            )}

            {/* project-home view */}
            {currentView === 'project-home' && activeProject && (
                <div className="h-full w-full pt-16 md:pt-20">
                    <ProjectHome
                      project={activeProject}
                      planSteps={planSteps}
                      activities={activities}
                      knowledge={knowledge}
                    />
                </div>
            )}
        </main>
      </div>

      {appToDisconnect && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center animate-in fade-in">
              <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl border border-gray-100 dark:border-white/10 flex flex-col gap-4">
                  <h3 className="text-xl font-bold text-black dark:text-white">Disconnect App</h3>
                  <p className="text-black dark:text-white text-sm">
                      Would you like to disconnect <strong>{appToDisconnect.name}</strong>? 
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">
                    This will delete the access tokens from the database.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                      <button 
                          onClick={() => setAppToDisconnect(null)}
                          className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 text-black dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                          Cancel
                      </button>
                      <button 
                          onClick={confirmDisconnect}
                          className="flex-1 px-4 py-2.5 rounded-xl bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black font-medium transition-colors border border-transparent"
                      >
                          Disconnect
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={() => setShowCreateProject(false)}
        onCreate={handleCreateProject}
        isLoading={isCreatingProject}
      />

      {showClearConfirm && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center animate-in fade-in">
              <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-xl border border-gray-100 dark:border-white/10 flex flex-col gap-4">
                  <h3 className="text-xl font-bold text-black dark:text-white">Clear All History</h3>
                  <p className="text-black dark:text-white text-sm">
                      Are you sure you want to delete <strong>all</strong> of your chat history? 
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">
                    This action is permanent and cannot be undone.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                      <button 
                          onClick={() => setShowClearConfirm(false)}
                          className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-800 text-black dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                          Cancel
                      </button>
                      <button 
                          onClick={handleClearHistory}
                          className="flex-1 px-4 py-2.5 rounded-xl bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black font-medium transition-colors border border-transparent"
                      >
                          Delete All
                      </button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

export default Dashboard;
